import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, Printer, HelpCircle } from "lucide-react";

/**
 * /app/help — Host quick-start guide.
 *
 * Loads the markdown file matching the current i18n language from
 * /public/help/host_guide_{lang}.md, renders with react-markdown,
 * and offers Print (→ Save as PDF) + Download .md actions.
 *
 * Video embedding: any markdown image whose URL ends with .webm, .mp4 or
 * .mov is rendered as an inline HTML5 `<video controls>` element. This
 * keeps the .md source portable (still renders on GitHub, Notion, etc.)
 * while giving hosts real playable tutorial clips inside the guide UI.
 */

const VIDEO_EXT = /\.(webm|mp4|mov)$/i;

// Poster mapping: which existing screenshot to show as thumbnail per video.
// Falls back to a generic scanner shot if the specific video is unmapped.
const VIDEO_POSTERS = {
  scan: "/help/screenshots/05-scanner.png",
  checkin: "/help/screenshots/04-calendar.png",
  alloggiati: "/help/screenshots/06-alloggiati.png",
};

const mdComponents = {
  // The page already renders an <h1> in its own header (localized title).
  // Demote the markdown top-level H1 to H2 so we don't stack two H1s.
  h1({ node, ...props }) {
    return <h2 className="!mt-2" {...props} />;
  },
  img({ node, ...props }) {
    const src = props.src || "";
    if (VIDEO_EXT.test(src)) {
      const key = src.split("/").pop().replace(VIDEO_EXT, "");
      const poster = VIDEO_POSTERS[key];
      return (
        <video
          src={src}
          controls
          preload="metadata"
          poster={poster}
          className="w-full rounded-lg border border-slate-200 my-6 shadow-sm bg-slate-100"
          aria-label={props.alt || ""}
          data-testid={`help-video-${key}`}
        >
          {props.alt}
        </video>
      );
    }
    // Regular image (all props including alt come from remark, so it's fine)
    return <img {...props} />;
  },
};
export default function HelpPage() {
  const { i18n, t } = useTranslation();
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);

  const lang = ["it", "en", "fr", "de", "zh"].includes(i18n.language) ? i18n.language : "it";
  const url = `/help/host_guide_${lang}.md`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(url)
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(text => { if (!cancelled) setMd(text); })
      .catch(() => { if (!cancelled) setMd(`# ${t("help.load_error")}`); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, t]);

  const downloadMd = () => {
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `host_guide_${lang}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="help-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap print:hidden">
        <div>
          <p className="text-xs tracked text-blue-700 mb-2 inline-flex items-center gap-1.5">
            <HelpCircle className="w-3.5 h-3.5" /> {t("help.label")}
          </p>
          <h1 className="font-heading font-semibold text-4xl tracking-tight">{t("help.title")}</h1>
          <p className="text-slate-600 mt-2 text-sm">{t("help.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            data-testid="help-print"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Printer className="w-4 h-4" /> {t("help.print_pdf")}
          </button>
          <button
            onClick={downloadMd}
            data-testid="help-download"
            className="inline-flex items-center gap-2 border-2 border-slate-300 text-slate-700 px-4 py-2.5 rounded-lg hover:bg-slate-50 text-sm"
          >
            <Download className="w-4 h-4" /> {t("help.download_md")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-12 text-center">{t("help.loading")}</div>
      ) : (
        <article className="markdown-body prose prose-stone max-w-none" data-testid="help-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{md}</ReactMarkdown>
        </article>
      )}
    </div>
  );
}
